import React, { useState, useEffect } from 'react';
import { settingsService } from '../../services/settingsService';

const TIPE_AKUN_OPTIONS = ['Aset', 'Kewajiban', 'Ekuitas', 'Pendapatan', 'Beban'];
const SALDO_NORMAL_OPTIONS = ['Debet', 'Kredit'];

function COA() {
  const [coa, setCoa] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [search, setSearch] = useState('');
  const [filterTipe, setFilterTipe] = useState('');
  
  const [form, setForm] = useState({
    kodeAkun: '',
    namaAkun: '',
    tipeAkun: 'Aset',
    kelompok: '',
    parentKode: '',
    level: 1,
    isDetail: true,
    saldoNormal: 'Debet'
  });

  useEffect(() => {
    fetchCOA();
  }, []);

  const fetchCOA = async () => {
    try {
      setLoading(true);
      const res = await settingsService.getCOA();
      console.log('COA API response:', res.data);
      // Handle nested data structure: res.data.data.data
      let data = res.data.data;
      if (data && !Array.isArray(data) && Array.isArray(data.data)) {
        data = data.data;
      }
      console.log('COA data:', data, 'Is array:', Array.isArray(data), 'Length:', data?.length);
      setCoa(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching COA:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveCOA = async () => {
    if (!form.kodeAkun || !form.namaAkun) {
      alert('Kode akun dan nama akun wajib diisi');
      return;
    }
    setSaving(true);
    try {
      await settingsService.saveCOA(form);
      setShowForm(false);
      resetForm();
      fetchCOA();
      alert('Akun berhasil disimpan');
    } catch (error) {
      alert(error.response?.data?.message || 'Gagal menyimpan akun');
    } finally {
      setSaving(false);
    }
  };

  const editCOA = (item) => {
    setForm({
      kodeAkun: item.KodeAkun,
      namaAkun: item.NamaAkun,
      tipeAkun: item.TipeAkun,
      kelompok: item.Kelompok || '',
      parentKode: item.ParentKode || '',
      level: item.Level || 1,
      isDetail: item.IsDetail !== false,
      saldoNormal: item.SaldoNormal || 'Debet'
    });
    setIsEdit(true);
    setShowForm(true);
  };

  const resetForm = () => {
    setForm({
      kodeAkun: '',
      namaAkun: '',
      tipeAkun: 'Aset',
      kelompok: '',
      parentKode: '',
      level: 1,
      isDetail: true,
      saldoNormal: 'Debet'
    });
    setIsEdit(false);
  };

  const getTipeBadgeColor = (tipe) => {
    const colors = {
      'Aset': 'primary',
      'Kewajiban': 'danger',
      'Ekuitas': 'success',
      'Pendapatan': 'info',
      'Beban': 'warning'
    };
    return colors[tipe] || 'secondary';
  };

  const filteredCOA = Array.isArray(coa) 
    ? coa.filter(c => {
        if (!c || !c.KodeAkun || !c.NamaAkun) return false;
        const matchSearch = !search || 
          (c.KodeAkun && c.KodeAkun.toLowerCase().includes(search.toLowerCase())) || 
          (c.NamaAkun && c.NamaAkun.toLowerCase().includes(search.toLowerCase()));
        const matchTipe = !filterTipe || c.TipeAkun === filterTipe;
        return matchSearch && matchTipe;
      })
    : [];

  // Group by tipe akun
  const groupedCOA = filteredCOA.reduce((acc, item) => {
    if (!acc[item.TipeAkun]) acc[item.TipeAkun] = [];
    acc[item.TipeAkun].push(item);
    return acc;
  }, {});

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h3 className="card-title"><i className="fas fa-book mr-2"></i>Chart of Accounts (COA)</h3>
        <button className="btn btn-primary btn-sm" onClick={() => { resetForm(); setShowForm(true); }}>
          <i className="fas fa-plus mr-1"></i> Tambah Akun
        </button>
      </div>
      <div className="card-body">
        {/* Filter */}
        <div className="row mb-3">
          <div className="col-md-4">
            <input 
              type="text" 
              className="form-control" 
              placeholder="Cari kode atau nama akun..." 
              value={search} 
              onChange={e => setSearch(e.target.value)} 
            />
          </div>
          <div className="col-md-3">
            <select className="form-control" value={filterTipe} onChange={e => setFilterTipe(e.target.value)}>
              <option value="">Semua Tipe</option>
              {TIPE_AKUN_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="col-md-5 text-right">
            <small className="text-muted">Total {filteredCOA.length} akun</small>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-3"><i className="fas fa-spinner fa-spin mr-2"></i>Memuat...</div>
        ) : (
          <div className="table-responsive" style={{ maxHeight: '600px', overflow: 'auto' }}>
            <table className="table table-bordered table-sm table-hover">
              <thead className="thead-dark sticky-top">
                <tr>
                  <th>Kode Akun</th>
                  <th>Nama Akun</th>
                  <th>Tipe</th>
                  <th>Saldo Normal</th>
                  <th>Level</th>
                  <th>Detail</th>
                  <th>Parent</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {filteredCOA.length > 0 ? (
                  filteredCOA.map(c => (
                    <tr key={c.KodeAkun} className={c.Level === 1 ? 'table-primary font-weight-bold' : ''}>
                      <td className="text-monospace">{c.KodeAkun}</td>
                      <td style={{ paddingLeft: `${(c.Level || 1) * 15}px` }}>
                        {c.Level === 1 ? <strong>{c.NamaAkun}</strong> : c.NamaAkun}
                      </td>
                      <td><span className={`badge badge-${getTipeBadgeColor(c.TipeAkun)}`}>{c.TipeAkun}</span></td>
                      <td><span className="badge badge-light">{c.SaldoNormal}</span></td>
                      <td>{c.Level}</td>
                      <td>{c.IsDetail ? <i className="fas fa-check-circle text-success"></i> : <i className="fas fa-folder text-muted"></i>}</td>
                      <td>{c.ParentKode || '-'}</td>
                      <td>
                        <button className="btn btn-warning btn-xs" onClick={() => editCOA(c)}>
                          <i className="fas fa-edit"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center">Belum ada data COA. Klik "Tambah Akun" untuk membuat.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Form Modal */}
        {showForm && (
          <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">{isEdit ? 'Edit' : 'Tambah'} Akun COA</h5>
                  <button type="button" className="close text-white" onClick={() => setShowForm(false)}>
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Kode Akun *</label>
                        <input 
                          type="text" 
                          className="form-control" 
                          value={form.kodeAkun} 
                          onChange={e => setForm({...form, kodeAkun: e.target.value})}
                          placeholder="Contoh: 1101, 3100"
                          disabled={isEdit}
                        />
                        <small className="text-muted">Kode unik, tidak bisa diubah setelah dibuat</small>
                      </div>
                      <div className="form-group">
                        <label>Nama Akun *</label>
                        <input 
                          type="text" 
                          className="form-control" 
                          value={form.namaAkun} 
                          onChange={e => setForm({...form, namaAkun: e.target.value})}
                          placeholder="Contoh: Kas, Bank, Simpanan Pokok"
                        />
                      </div>
                      <div className="form-group">
                        <label>Tipe Akun *</label>
                        <select className="form-control" value={form.tipeAkun} onChange={e => setForm({...form, tipeAkun: e.target.value})}>
                          {TIPE_AKUN_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Saldo Normal *</label>
                        <select className="form-control" value={form.saldoNormal} onChange={e => setForm({...form, saldoNormal: e.target.value})}>
                          {SALDO_NORMAL_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Kelompok</label>
                        <input 
                          type="text" 
                          className="form-control" 
                          value={form.kelompok} 
                          onChange={e => setForm({...form, kelompok: e.target.value})}
                          placeholder="Contoh: Aktiva Lancar, Modal"
                        />
                      </div>
                      <div className="form-group">
                        <label>Parent Kode (Akun Induk)</label>
                        <select className="form-control" value={form.parentKode} onChange={e => setForm({...form, parentKode: e.target.value})}>
                          <option value="">-- Tidak Ada --</option>
                          {coa.filter(c => !c.IsDetail).map(c => (
                            <option key={c.KodeAkun} value={c.KodeAkun}>{c.KodeAkun} - {c.NamaAkun}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Level</label>
                        <input 
                          type="number" 
                          className="form-control" 
                          value={form.level} 
                          onChange={e => setForm({...form, level: parseInt(e.target.value) || 1})}
                          min="1" 
                          max="5"
                        />
                        <small className="text-muted">Level 1 = Header, Level 2+ = Detail/Sub-akun</small>
                      </div>
                      <div className="form-group">
                        <div className="custom-control custom-checkbox">
                          <input 
                            type="checkbox" 
                            className="custom-control-input" 
                            id="isDetail" 
                            checked={form.isDetail}
                            onChange={e => setForm({...form, isDetail: e.target.checked})}
                          />
                          <label className="custom-control-label" htmlFor="isDetail">
                            Akun Detail (bisa diposting)
                          </label>
                        </div>
                        <small className="text-muted d-block">
                          Uncheck jika ini akun header/induk
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="alert alert-info mt-3">
                    <h6>Preview:</h6>
                    <p className="mb-0">
                      <strong>{form.kodeAkun || '???'}</strong> - {form.namaAkun || '???'} 
                      <span className={`badge badge-${getTipeBadgeColor(form.tipeAkun)} ml-2`}>{form.tipeAkun}</span>
                      <span className="badge badge-light ml-1">{form.saldoNormal}</span>
                    </p>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Batal</button>
                  <button type="button" className="btn btn-primary" onClick={saveCOA} disabled={saving}>
                    {saving ? <i className="fas fa-spinner fa-spin mr-1"></i> : <i className="fas fa-save mr-1"></i>}
                    Simpan Akun
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default COA;
